import { LayoutGrid } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { PRODUCT_ACCENTS } from "../../styles/brand";

const PRODUCTS = [
  {
    id: "schoolconferencego",
    name: "SchoolConferenceGo",
    href: "/",
    blurb: "Parent-teacher conferences",
    accent: PRODUCT_ACCENTS.schoolconferencego,
    current: true,
  },
  {
    id: "signupgo",
    name: "SignUpGo",
    href: "https://signupgo.com",
    blurb: "Signups & reminders",
    accent: PRODUCT_ACCENTS.signupgo,
    current: false,
  },
  {
    id: "eventticketgo",
    name: "EventTicketGo",
    href: "https://eventticketgo.com",
    blurb: "Tickets & seating",
    accent: PRODUCT_ACCENTS.eventticketgo,
    current: false,
  },
] as const;

type AppSwitcherProps = {
  className?: string;
};

/**
 * B-4.11 app switcher — waffle control left of the lockup.
 * Opens the suite panel so users can quick-change products.
 */
export function AppSwitcher({ className = "" }: AppSwitcherProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className={`app-switcher ${className}`.trim()} ref={rootRef}>
      <button
        type="button"
        className="app-switcher__btn"
        aria-label="Switch Sizzle products"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
      >
        <LayoutGrid size={22} aria-hidden />
      </button>
      {open && (
        <div id={menuId} role="menu" className="app-switcher__menu">
          <p className="app-switcher__heading">Sizzle suite</p>
          <ul className="app-switcher__list">
            {PRODUCTS.map((product) => (
              <li key={product.id}>
                <a
                  role="menuitem"
                  href={product.href}
                  className={`app-switcher__item${product.current ? " is-current" : ""}`}
                  aria-current={product.current ? "page" : undefined}
                  onClick={() => setOpen(false)}
                  {...(product.href.startsWith("http")
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                >
                  <span
                    className="app-switcher__dot"
                    style={{ background: product.accent }}
                    aria-hidden
                  />
                  <span className="app-switcher__copy">
                    <span className="app-switcher__name">{product.name}</span>
                    <span className="app-switcher__blurb">
                      {product.current ? "You're here · " : ""}
                      {product.blurb}
                    </span>
                  </span>
                </a>
              </li>
            ))}
          </ul>
          <div className="app-switcher__footer">
            <a href="https://timetosizzle.com/sizzlego" target="_blank" rel="noopener noreferrer">
              SizzleGo — all products
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
